export const SOCIAL_SECURITY_TEMPLATE = `# Manage SSA Survivor Benefits for {{deceased_first_name}}

We know there is a lot to handle right now. This guide is here to help you navigate Social Security survivor benefits and find out what support is available to you. SSA will make the final eligibility decision, but this guide helps you prepare for the call and know what to ask.

These benefits are separate from your role as an executor. Instead, they focus on your relationship with {{deceased_first_name}} and how the Social Security Administration (SSA) provides for family members.

# Contact the SSA

| | |
| --- | --- |
| Phone | 1-800-772-1213 |
| TTY | 1-800-325-0778 |

# Critical First Steps

- Do not spend any SSA payments that arrive after the passing.
- Notify the bank of the death.
- Ask the bank to help return post-passing funds to the SSA.

# Phase 1: Setup

**Step 1: Set up your online portal**
- Create or log in to your account at ssa.gov.
- Use the portal to request your required survivor benefits interview.
- Upload documents directly to the portal to speed up your application.

{{#if is_spouse}}
# Determining Your Eligibility as a Surviving Spouse

You may be eligible for certain benefits. Contact the SSA to confirm if you can apply for:
- One-time death payment of $255
- Monthly survivor benefits, depending on the specific criteria SSA looks for.

## Your Next Steps

Before calling, confirm with the funeral home if they have already reported the death. If they have, you only need to call SSA to apply for benefits, not to report the passing.

**Before you call: Gather these documents.**
- Your Social Security number
- {{deceased_first_name}}'s Social Security number: {{deceased_ss_number}}
- Certified copy of the death certificate
- Marriage certificate
- Your birth certificate or proof of age
- The deceased person's W-2 forms or self-employment tax return for the last year.
- Bank account and routing number for an account in your name (not the deceased's or the estate's).

**Call SSA:**
Refer to the Contact the SSA section for official phone numbers.

If you need an interpreter, say "Interpreter" when the call is answered. This is a free service provided by the SSA. Tip for calling: To avoid long hold times on the main 800-number, you can specifically use the automated system to "Request an Appointment" or use the local office's direct number found via the Office Locator.

**Say:**

> "I am calling to apply for survivor benefits for my spouse, {{deceased_full_name}}. I believe the funeral home has already reported the death, but I would like to start my application and confirm if any payments need to be returned."

**Ask SSA:**
- Am I eligible for monthly survivor benefits?
- Am I eligible for the $255 lump-sum death payment?
- Were any payments sent after {{deceased_first_name}} passed away?
- Does anything need to be returned?
- What documents do you need from me?
- Can this be completed by phone, or do I need an appointment?
{{/if}}

{{#if is_parent}}
# Ask SSA About Dependent Parent Survivor Benefits

As a parent of {{deceased_first_name}}, you may be eligible for survivor benefits only in specific situations. SSA includes dependent parents as a possible survivor-benefit category, but SSA will need to confirm whether you qualify.

## Your Next Steps

**Call SSA:**
Refer to the Contact the SSA section for official phone numbers.

**Say:**

> "I am calling to ask about dependent parent benefits for my child, {{deceased_full_name}}. I believe the funeral home has already reported the death, but I would like to confirm if any payments need to be returned and if I qualify for benefits."

**Have ready:**
- Your Social Security number
- {{deceased_first_name}}'s Social Security number: {{deceased_ss_number}}
- Certified copy of the death certificate
- Proof of your age
- Documents showing your relationship to {{deceased_first_name}}
- IRS tax returns and Form SSA-760-F4 to prove {{deceased_first_name}} provided at least one-half of your financial support. You should review and download the 'Certificate of Support' PDF before calling. You can find both of these specific documents by searching for their names on the SSA Forms Search Page: ssa.gov/forms.
{{/if}}

{{#if is_other}}
# Eligibility

Social Security survivor benefits are usually claimed by eligible survivors, such as a spouse, certain children, or a dependent parent. If these specific categories don't apply to you, you might not be the one to receive the benefits directly, but you can still help ensure everything is in order.

You can still call SSA to confirm:
- Whether the death has been reported
- Whether any payments need to be returned
- Whether another family member may need to apply

**Call SSA:**
Refer to the Contact the SSA section for official phone numbers.

**Say:**

> "I am calling to confirm next steps after the death of {{deceased_full_name}}. I believe the funeral home has already reported the death, but I would like to confirm if any payments need to be returned and if another family member needs to apply for survivor benefits."
{{/if}}

# Official SSA Digital Resources

| Resource | Link |
| --- | --- |
| SSA Office Locator | ssa.gov/locator |
| Survivor Benefits Information | ssa.gov/survivor |
| SSA Forms Search Page | ssa.gov/forms |

# Quick Checklist

- [ ] Call SSA at 1-800-772-1213
- [ ] Ask whether survivor benefits apply
{{#if is_spouse}}
- [ ] Ask about the $255 lump-sum death payment (must be applied for within 2 years of death)
{{/if}}
- [ ] Ask whether any payments need to be returned
- [ ] It helps to have a notepad nearby to jot down what the SSA representative tells you.
- [ ] Save any confirmation number or appointment details
- [ ] Store your notes and confirmation numbers in Elayne after the call

# Important to Know

- Social Security decides eligibility based on your relationship and their rules. They will confirm whether you qualify and what you can receive.
- Since these benefits don't start automatically, the best way to make sure you receive them is to reach out to the SSA and begin the application process.
- Do not delay calling SSA if you are missing a document. You can start the process, and they will tell you exactly what is still needed.
- If there are multiple surviving family members or complex estate issues, contact a legal professional or your Elayne advisor for guidance.`;
